import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Usage de l'IA (TF-439 ; quotas TF-474). Toute fonction IA passe par la
 * passerelle de l'API, qui compte chaque requête et refuse ce que la direction
 * a coupé — l'IA est fermée à un compte tant qu'elle ne lui a pas accordé de
 * quota : l'écran lit ses comptes et ses états, il ne recalcule rien.
 */

export type FonctionIa = 'GENERATION_CONTENU' | 'TUTEUR' | 'CORRECTION_TEST' | 'TRANSCRIPTION';
export type EtatUsageCompte = 'DESACTIVE' | 'NON_AUTORISE' | 'QUOTA_ATTEINT' | 'AU_DELA_DU_SEUIL' | 'AUTORISE';

export interface RaccordementIa {
  fonction: FonctionIa;
  libelle: string;
  raccordee: boolean;
  prestataire: string | null;
}

export interface DesactivationIa {
  le: string;
  par: string | null;
  motif: string;
}

export interface ParFonctionIa {
  fonction: FonctionIa;
  libelle: string;
  envoyees: number;
}

/** Un compte formateur ou apprenant : son quota, ce qu'il a demandé (génération et tuteur). */
export interface LigneCompteIa {
  utilisateurId: string;
  nom: string;
  prenom: string;
  initiales: string;
  email: string;
  compteActif: boolean;
  sites: string[];
  desactivation: DesactivationIa | null;
  /** Requêtes accordées par jour ; `null` : l'IA lui est fermée. */
  quota: number | null;
  envoyees: { jour: number; semaine: number; mois: number };
  refusees: { jour: number; mois: number };
  parFonction: ParFonctionIa[];
  derniereRequete: string | null;
  etat: EtatUsageCompte;
  etatLibelle: string;
}

export interface LigneFormateurIa extends LigneCompteIa {
  formateurId: string | null;
  statut: 'SALARIE' | 'INDEPENDANT' | null;
  /** Seuil d'alerte applicable, et sa valeur propre (vide : celui de l'organisme). */
  seuil: number;
  seuilPropre: number | null;
  alerteOuverte: boolean;
}

export interface LigneApprenantIa extends LigneCompteIa {
  dossierId: string | null;
  formation: { id: string; titre: string } | null;
  /** En formation aujourd'hui : le quota par formation s'accorde à ces apprenants. */
  enFormation: boolean;
}

export interface LigneFormationIa {
  id: string;
  code: string;
  titre: string;
  niveau: string;
  active: boolean;
  desactivation: DesactivationIa | null;
  envoyees: { jour: number; mois: number };
  refuseesMois: number;
  parFonction: ParFonctionIa[];
}

export interface ReglageIa {
  seuilAlerteJour: number;
  /** Correction proposée des tests allumée pour l'organisme ; éteinte de base. */
  correctionTests: boolean;
  parDefaut: boolean;
  regleLe: string | null;
  reglePar: string | null;
  min: number;
  max: number;
}

/**
 * Le prestataire de l'IA générative : la clé est sur le serveur, le modèle est
 * choisi par la direction — sans choix, les modèles gratuits d'OpenRouter.
 */
export interface PrestataireIa {
  nom: string;
  cleConfiguree: boolean;
  /** Le choix de la direction, `null` sans choix. */
  modele: string | null;
  libelleModele: string | null;
  regleLe: string | null;
  reglePar: string | null;
  enVigueur: { modele: string; libelle: string; parDefaut: boolean };
}

export interface ReponseSupervisionIa {
  calculeLe: string;
  raccordement: RaccordementIa[];
  prestataire: PrestataireIa;
  reglage: ReglageIa;
  fenetres: { semaine: number; mois: number };
  conservationAns: number;
  compteurs: {
    requetesJour: number;
    requetesMois: number;
    refuseesMois: number;
    formateurs: number;
    formateursAutorises: number;
    apprenants: number;
    apprenantsAutorises: number;
    auDelaDuSeuil: number;
    quotasAtteints: number;
    comptesDesactives: number;
    alertesOuvertes: number;
    formationsDesactivees: number;
    autresMois: number;
  };
  autres: (ParFonctionIa & { refusees: number })[];
  formateurs: LigneFormateurIa[];
  apprenants: LigneApprenantIa[];
  formations: LigneFormationIa[];
}

const CLE = ['usage-ia'];

export function useSupervisionIa(site: string) {
  const parametres = new URLSearchParams();
  if (site) parametres.set('site', site);
  return useQuery({
    queryKey: [...CLE, parametres.toString()],
    queryFn: () => appelApi<ReponseSupervisionIa>(`/usage-ia?${parametres.toString()}`),
    placeholderData: keepPreviousData,
    // Les requêtes arrivent pendant que la direction regarde.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

function useEcritureSupervision<V, R>(ecrire: (variables: V) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ecrire,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE });
      void client.invalidateQueries({ queryKey: ['ia'] });
    },
  });
}

export function useReglerOrganismeIa() {
  return useEcritureSupervision((corps: { seuilAlerteJour: number; correctionTests: boolean }) =>
    appelApi<ReglageIa>('/usage-ia/reglage', { methode: 'PUT', corps }),
  );
}

/** `null` : plus de choix, les modèles gratuits d'OpenRouter reprennent. */
export function useReglerModeleIa() {
  return useEcritureSupervision((corps: { modele: string | null }) =>
    appelApi<PrestataireIa>('/usage-ia/modele', { methode: 'PUT', corps }),
  );
}

/** Quota d'un compte (vide : IA fermée) et, pour un formateur, seuil d'alerte propre. */
export function useReglerCompteIa() {
  return useEcritureSupervision(
    ({
      utilisateurId,
      ...corps
    }: {
      utilisateurId: string;
      quotaJour: number | null;
      seuilAlerteJour: number | null;
    }) =>
      appelApi<{ message: string }>(`/usage-ia/comptes/${encodeURIComponent(utilisateurId)}`, {
        methode: 'PUT',
        corps,
      }),
  );
}

/** D'un geste, le quota des apprenants en formation d'une formation ; vide, l'IA leur est refermée. */
export function useQuotaApprenantsFormation() {
  return useEcritureSupervision(
    ({ formationId, ...corps }: { formationId: string; quotaJour: number | null; site?: string }) =>
      appelApi<{ message: string; comptes: number }>(
        `/usage-ia/formations/${encodeURIComponent(formationId)}/quota-apprenants`,
        { methode: 'PUT', corps },
      ),
  );
}

export type CibleActivation = 'formateurs' | 'formations';

export function useActivationIa() {
  return useEcritureSupervision(
    ({ cible, id, desactiver, motif }: { cible: CibleActivation; id: string; desactiver: boolean; motif: string }) =>
      appelApi<{ message: string }>(
        `/usage-ia/${cible}/${encodeURIComponent(id)}/${desactiver ? 'desactivation' : 'reactivation'}`,
        { methode: 'POST', corps: { motif } },
      ),
  );
}

// ---------------------------------------------------------------------------
// Côté formateur et apprenant
// ---------------------------------------------------------------------------

export interface FormationIa {
  id: string;
  code: string;
  titre: string;
  niveau: string;
  desactivee: { motif: string } | null;
}

/** Où en est un compte soumis au quota ; `quota` vide : l'IA ne lui est pas ouverte. */
export interface CompteurIa {
  /** Jamais « au-delà du seuil » : seule la direction en est alertée. */
  etat: Exclude<EtatUsageCompte, 'AU_DELA_DU_SEUIL'>;
  desactivee: { le: string; motif: string } | null;
  envoyeesDuJour: number;
  quota: number | null;
  /** Seuil d'alerte de la direction : celui d'un formateur ; un apprenant n'en a pas. */
  seuil: number | null;
}

export interface EtatIa {
  fonctions: RaccordementIa[];
  /** `null` pour la direction, qui n'a pas de quota. */
  compte: CompteurIa | null;
  /** L'apprenant passe un quiz : le tuteur lui est fermé, au plus tard jusqu'à `jusqua`. */
  tuteurFerme: { motif: string; jusqua: string } | null;
  formationApprenant: FormationIa | null;
  formations: FormationIa[];
  typesContenu: { code: string; libelle: string }[];
}

export interface ReponseFonctionIa {
  texte: string;
  prestataire: string;
  compte: CompteurIa | null;
  /** Réponse du tuteur seulement : à renvoyer avec elle dans la conversation. */
  sceau?: string;
}

export function useEtatIa() {
  return useQuery({
    queryKey: ['ia', 'etat'],
    queryFn: () => appelApi<EtatIa>('/ia/etat'),
    refetchOnWindowFocus: true,
    // Le quiz peut se terminer dans un autre onglet : le tuteur rouvre sans recharger la page.
    refetchInterval: (requete) => (requete.state.data?.tuteurFerme ? 30_000 : false),
  });
}

function useFonctionIa<V>(chemin: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: V) => appelApi<ReponseFonctionIa>(chemin, { methode: 'POST', corps }),
    // Le compteur du jour et l'état ont pu changer, refus compris.
    onSettled: () => void client.invalidateQueries({ queryKey: ['ia', 'etat'] }),
  });
}

export function useGenerationIa() {
  return useFonctionIa<{ formationId: string; type: string; sujet: string }>('/ia/generation');
}

/**
 * Un échange déjà affiché, renvoyé au tuteur avec la question suivante : l'API
 * n'en garde aucun. Une réponse du tuteur revient avec le sceau qu'elle portait.
 */
export interface EchangeTuteur {
  role: 'QUESTION' | 'REPONSE';
  texte: string;
  sceau?: string;
}

/** Ce que l'API accepte (`HISTORIQUE_TUTEUR_MAX`, `LONGUEUR_ECHANGE_TUTEUR_MAX`). */
export const HISTORIQUE_TUTEUR_MAX = 10;
export const LONGUEUR_ECHANGE_TUTEUR_MAX = 12000;

export function useQuestionTuteur() {
  return useFonctionIa<{ question: string; historique: EchangeTuteur[] }>('/ia/tuteur');
}

/** « 12 requêtes IA aujourd'hui sur 60 accordées par la direction · seuil d'alerte : 50 par jour ». */
export function texteCompteur(compteur: CompteurIa): string {
  const base = `${compteur.envoyeesDuJour} requête${compteur.envoyeesDuJour > 1 ? 's' : ''} IA aujourd’hui`;
  const quota = compteur.quota === null ? '' : ` sur ${compteur.quota} accordée${compteur.quota > 1 ? 's' : ''} par la direction`;
  const seuil = compteur.seuil === null ? '' : ` · seuil d’alerte : ${compteur.seuil} par jour`;
  return `${base}${quota}${seuil}`;
}
